/* eslint-disable camelcase */
import dayjs from 'dayjs'

import { IConvertedTransactions, IPaymentsHistoryItemDTO } from 'services/api/ProfileApi/types/types'

import { countriesFieldName, countriesList, paymentNameSeparator, qrCodeExpirationTime } from 'shared/constants/payment'
import { AvailableCryptoCurrenciesList } from 'shared/models/Crypto'
import { ExtraSchema, UploadScreenshotResponse } from 'shared/models/Payments'

import { cryptoCurrenciesWhiteList, paymentSystemMethodsSortList } from './data'
import { DepositResponseNames, FieldTypes } from './types/common'
import {
  ICashierItem,
  ICashierItemDTO,
  ICryptoCurrencyListDTO,
  ICryptoCurrencyWithMinAndMaxItem,
  ICryptoEstimateAmount,
  ICryptoEstimateAmountDTO,
  ICryptoMinAmount,
  ICryptoMinAmountDTO,
  IDefaultDataConverted,
  IDefaultDataResponse,
  IDeposit,
  IDepositDTO,
  ISystemFieldsDTO,
  ISystemListItem,
  ISystemListItemDTO,
  UploadScreenshotDTO,
} from './types/types'

export class PaymentConverter {
  convertDeposit = (data: IDepositDTO): IDeposit => ({
    type: data.type,
    redirectLink: data.type === DepositResponseNames.URL ? data.payment_invocation : null,
    qrCodeData:
      data.type === DepositResponseNames.QR
        ? {
            url: data.payment_invocation,
            expirationDate: qrCodeExpirationTime + Date.now(),
          }
        : null,
    walletAddress: data.type === DepositResponseNames.WALLET_ADDRESS ? data.payment_invocation : null,
    externalId: data.external_id,
    variables:
      data.type === DepositResponseNames.WALLET_ADDRESS
        ? {
            fiatCurrency: String(data.variables?.fiat_currency),
            cryptoAmount: Number(data.variables?.crypto_amount),
            fiatAmount: Number(data.variables?.fiat_amount),
          }
        : null,
  })

  convertExtraSchemaFields = (extraSchema: ISystemFieldsDTO, extraName?: string): ExtraSchema[] => {
    const extraSchemaArray = Object.entries(extraSchema)

    return (
      (
        extraSchemaArray.map(([fieldKey, fieldValue]) => {
          const fieldName = extraName ? `${extraName}${paymentNameSeparator}${fieldKey}` : fieldKey
          if (typeof fieldValue.type !== 'string') {
            return this.convertExtraSchemaFields(fieldValue as ISystemFieldsDTO, fieldName)
          }
          let values = null
          let value = null
          let fieldDuplicate = null
          let validation = null
          let fieldType = 'input'

          if (Array.isArray(fieldValue.values)) values = fieldValue.values
          if (typeof fieldValue.field_duplicate === 'string') fieldDuplicate = fieldValue.field_duplicate
          if (typeof fieldValue.fieldType === 'string') fieldType = fieldValue.fieldType
          if (typeof fieldValue.value !== 'undefined') value = fieldValue.value
          if (typeof fieldValue.validation === 'string') validation = fieldValue.validation
          if (fieldName === countriesFieldName) {
            values = countriesList
            fieldType = FieldTypes.SELECTOR
          }
          if (fieldName === 'date') {
            fieldType = FieldTypes.DATE
          }

          return {
            name: fieldName,
            fieldType,
            value,
            values,
            fieldDuplicate,
            validation,
            type: fieldValue.type,
            placeholder: fieldValue.placeholder,
            endAdornmentType: fieldValue.endAdornmentType,
            minLength: isNaN(Number(fieldValue.min_length)) ? null : Number(fieldValue.min_length),
            maxLength: isNaN(Number(fieldValue.max_length)) ? null : Number(fieldValue.max_length),
            isOptional: fieldName === 'Description' || Boolean(fieldValue.optional),
            isDisabled: Boolean(fieldValue.disabled),
          }
        }) as ExtraSchema[]
      ).sort((a, b) => (a.fieldType !== FieldTypes.DATE && a.fieldType === b.fieldType ? 0 : -1)) ?? []
    )
  }

  convertSystemList = (data: ISystemListItemDTO[]): IDefaultDataConverted<ISystemListItem> => {
    const systemList = data
      ?.map(item => ({
        name: item.name,
        method: item.method,
        slug: `${item.name}_${item.method}`,
        currency: item.currency,
        min: item.min,
        max: item.max,
        fields: item.extra_schema ? this.convertExtraSchemaFields(item.extra_schema) : [],
        requisites: item.requisites ?? [],
        depositImagesEnabled: item.deposit_images_enabled,
        depositImagesRequired: item.deposit_images_required,
        depositImagesMaxCount: item.deposit_images_max_count,

        isDisabled: item.is_disabled,
      }))
      .filter(system => !system.isDisabled)
      .sort(
        (a, b) =>
          (paymentSystemMethodsSortList.indexOf(a.method) + 1 || Infinity) -
          (paymentSystemMethodsSortList.indexOf(b.method) + 1 || Infinity - 1),
      )

    return {
      page: 1,
      count: systemList?.length || 0,
      list: systemList,
    }
  }

  convertCryptoCurrencies = (data: ICryptoCurrencyListDTO): ICryptoCurrencyWithMinAndMaxItem[] =>
    data.selectedCurrencies
      ?.map(t => ({
        currency: t.toLowerCase(),
      }))
      .sort(
        (a, b) =>
          cryptoCurrenciesWhiteList.indexOf(a.currency as AvailableCryptoCurrenciesList) -
          cryptoCurrenciesWhiteList.indexOf(b.currency as AvailableCryptoCurrenciesList),
      )
      .filter(t => cryptoCurrenciesWhiteList.indexOf(t.currency as AvailableCryptoCurrenciesList) !== -1)

  convertCryptoMinAmount = (data: ICryptoMinAmountDTO): ICryptoMinAmount => ({
    currency: data.currency_from,
    min: data.min_amount,
    fiatAmount: data.fiat_equivalent,
  })

  convertCryptoEstimateAmount = (data: ICryptoEstimateAmountDTO): ICryptoEstimateAmount => ({
    currencyFrom: data.currency_from,
    amountFrom: data.amount_from,
    currencyTo: data.currency_to,
    estimatedAmount: data.estimated_amount,
  })

  convertCashiersList = (data: ICashierItemDTO[]): ICashierItem[] =>
    data.map(it => ({
      id: it.id,
      username: it.username,
    }))

  convertNewTransfersList = (data: IDefaultDataResponse<IPaymentsHistoryItemDTO>): IConvertedTransactions => {
    const { page, results, count } = data

    const list =
      results?.map(item => ({
        id: String(item.id),
        amount: Math.floor(+item.amount * 100) / 100,
        currency: item.currency,
        createdAt: dayjs(item.created_at).format('DD/MM/YYYY HH:mm'),
        status: item.status,
        type: item.type,
        comment: item.manual_comment ?? '',
        paymentSystem: item.system_name,
        paymentMethod: item.system_method,
      })) || []

    return {
      page,
      count,
      history: list,
    }
  }

  convertUploadScreenshots = (data: UploadScreenshotDTO[]): UploadScreenshotResponse[] =>
    data.map(screen => ({
      index: screen.index,
      originalName: screen.original_name,
      status: screen.status,
      statusCode: screen.status_code,
      errorMessage: screen.error_message,
    }))
}
